import { useEffect, useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import TitleBar from '../../../components/TitleBar';
import { LevelMappingForm } from './forms';
import { Footer } from '../../../components/common/Footer';
import { BorderedSection } from '../../../components';
import { useParams, useSearchParams } from 'react-router-dom';
import { DocumentNode, useMutation, useQuery } from '@apollo/client';
import {
  FETCH_LEVEL_DEPENDENCY_MATRIX,
  UPDATE_DIVISION_LEVEL_PERCENTAGE,
  UPDATE_PRODUCT_GROUP_LEVEL_PERCENTAGE,
  UPDATE_PRODUCT_LEVEL_PERCENTAGE,
} from '../queries';
import { DependencyMatrix, LocationAuxs, Products } from './DependencyMatrix';
import { Comment } from '../../../components/assets';
import AntModal from '../../../components/generic/AntModal';
import FinancialData from '../../../components/common/FinancialData';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../components/generic/Table/TableV3';
import { camelCase } from 'lodash';
import {
  getLevelLocationData,
  tableSortingMethods,
  updatePreviousLocations,
} from '../../../utils';
import DependenciesTitle from '../../../components/common/DependenciesTitle';
import { GET_CORPOTATE_CHART } from '../../../constants/queryVariables';
import { useUserContext } from '../../../context/PermissionsContext';
import QueryErrorBoundary from '../../../components/error-handling';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import PercentageFormatter from '../../../components/common/PercentageFormatter';
import TitleUpdater from '../../../components/common/TitleUpdater';

const columnsPerPage = 8;

function LevelLocationMapping() {
  const [modalOpen, setModalOpen] = useState(false);
  const [product, setProduct] = useState<string>('');
  const params = useParams();
  let [searchParams] = useSearchParams();
  const divisionId = params.divisionId;
  const parentLocation = params.parentLocation!;
  const level = parseInt(searchParams.get('level')!);
  const locationName = searchParams.get('locationName')!;
  const divisionName = searchParams.get('divisionName')!;
  const locationId = searchParams.get('locationId')!;
  const prevLocationId = searchParams.get('prevLocationId') || '';
  const prevLocationName = searchParams.get('prevLocationName') || '';

  const {
    prevLocationId: updatedPrevLocationId,
    prevLocationName: updatedPrevLocationName,
  } = updatePreviousLocations(
    locationId,
    locationName,
    prevLocationId,
    prevLocationName,
  );

  const { locationId: levelLocationId, locationName: levelLocationName } =
    getLevelLocationData(updatedPrevLocationId, updatedPrevLocationName, level);

  const [columnsState, setColumnsState] = useState<LocationAuxs[]>([]);
  const [item, setItem] = useState<any>();
  const [isEditing, setIsEditing] = useState(false);

  const { canViewFinancials, canEdit } = useUserContext();

  const { data, loading, error } = useQuery<DependencyMatrix>(
    FETCH_LEVEL_DEPENDENCY_MATRIX,
    {
      variables: {
        division: { uuid: divisionId },
        level,
        location: { uuid: levelLocationId },
        parentLocation: { uuid: parentLocation },
      },
      skip: !divisionId || !levelLocationId || !parentLocation,
    },
  );

  const [updateLevelPercentageDivision, { loading: updateDivisionLoading }] =
    useMutation(UPDATE_DIVISION_LEVEL_PERCENTAGE, {
      refetchQueries: [
        'DivisionSelectionDashboard',
        'GetMapping',
        GET_CORPOTATE_CHART,
      ],
    });

  const [
    updateLevelPercentageProductGroup,
    { loading: updateProductGroupLoading },
  ] = useMutation(UPDATE_PRODUCT_GROUP_LEVEL_PERCENTAGE, {
    refetchQueries: [
      'DivisionSelectionDashboard',
      'GetMapping',
      GET_CORPOTATE_CHART,
    ],
  });

  const [updateLevelPercentageProduct, { loading: updateProductLoading }] =
    useMutation(UPDATE_PRODUCT_LEVEL_PERCENTAGE, {
      refetchQueries: [
        'DivisionSelectionDashboard',
        'GetMapping',
        GET_CORPOTATE_CHART,
      ],
    });

  const mutations = {
    DivisionMatrix: updateLevelPercentageDivision,
    ProductGroupMatrix: updateLevelPercentageProductGroup,
    ProductMatrix: updateLevelPercentageProduct,
  };

  const loadingStates =
    updateDivisionLoading || updateProductGroupLoading || updateProductLoading;

  useEffect(() => {
    if (columnsState.length === 0 && data !== undefined) {
      setColumnsState(dependencies[0]?.locationAuxs?.slice(0, columnsPerPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleCancel = () => {
    setModalOpen(false);
  };

  const dependencies: Products[] = [
    data?.findDivisionDependencyMatrix?.division || ([] as unknown as Products),
  ];

  const handleAddComment = () => {
    setIsEditing(false);
    setModalOpen(true);
    const { children, ...rest } = dependencies[0];
    setItem(rest);
    setProduct(rest.__typename);
  };

  const locationColumns: ColumnsType<Products> =
    data?.findDivisionDependencyMatrix?.division?.locationAuxs?.map(
      (location, index: number) => ({
        title: (
          <DependenciesTitle name={location.name} comment={location.comment} />
        ),
        dataIndex: 'locationAuxs',
        key: location.key,
        colIndex: index,
        editable: true,
        width: 160,
        align: 'right',
        sorter: canViewFinancials
          ? (a, b) => tableSortingMethods.number(a.revenue, b.revenue)
          : undefined,
        showSorterTooltip: false,
        render: (_, record) => {
          const locations = [...record.locationAuxs];

          const actualLocation = locations.find(
            (loc) => loc?.key === location?.key,
          );
          return (
            <div className="custom-table--column">
              <PercentageFormatter value={actualLocation?.percentage} />
              <FinancialData text={actualLocation?.supportedRevenue!} />
            </div>
          );
        },
      }),
    ) || [];

  const columns: ColumnsType<Products> = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      fixed: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Revenue Supported',
      dataIndex: 'revenue',
      key: 'revenue',
      width: 120,
      fixed: 'left',
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.revenue, b.revenue)
        : undefined,
      render: (text: number) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    ...locationColumns,
  ];

  const dependenciesData = [...dependencies];

  const allLocations = dependenciesData[0]?.locationAuxs;

  const formFields = allLocations?.map((location) => {
    return {
      label: location.name,
      key: camelCase(location.name),
      type: 'percentage',
      defaultValue: location.percentage,
    };
  });

  const modifyVariables = ({
    currentRecord,
    locationId: locationAuxId,
  }: ModifyVariablesArgs) => {
    const { __typename } = currentRecord;

    const mutation = mutations[
      __typename as keyof typeof mutations
    ] as unknown as DocumentNode;

    switch (__typename) {
      case 'DivisionMatrix':
        return {
          division: { uuid: currentRecord.key },
          location: { uuid: locationId },
          locationAux: { uuid: locationAuxId },
          parentLocation: { uuid: parentLocation },
          level,
          mutation,
        };
      case 'ProductGroupMatrix':
        return {
          productGroup: { uuid: currentRecord.key },
          location: { uuid: locationId },
          locationAux: { uuid: locationAuxId },
          parentLocation: { uuid: parentLocation },
          level,
          mutation,
        };
      default:
        return {
          product: { uuid: currentRecord.key },
          location: { uuid: locationId },
          locationAux: { uuid: locationAuxId },
          parentLocation: { uuid: parentLocation },
          level,
          mutation,
        };
    }
  };

  const baseColumns: ExportColumn[] = [
    { key: 'name', header: 'Division', type: 'text', width: 45 },
    {
      key: 'revenue',
      header: 'Company Wide Revenue',
      type: 'money',
      width: 40,
    },
  ];

  const mappingColumns: ExportColumn[] = [];

  if (dependenciesData) {
    dependenciesData[0].locationAuxs?.forEach((location, index) => {
      mappingColumns.push({
        key: location.key,
        header: location.name,
        type: 'percentage',
        nestedKey: `locationAuxs.${index}.percentage`,
      });
    });
  }

  return (
    <div>
      <TitleUpdater title={`Level ${level} Dependency Mapping`} />
      <TitleBar
        title="Dependency Mapping"
        subtitle={`Level ${level} Mapping // ${divisionName} // ${levelLocationName}`}
        brief="In the matrix, enter the degree to which each division, product group, or product depends
                      on the locations that were identified as supporting finished goods. MEO assumes that finished
                      goods can only be sold to final customers from one location, therefore, the sum of the percentages
                       in the columns for any given division, product group, or product, cannot exceed 100%. This avoids
                        double counting."
      />
      <QueryErrorBoundary error={error}>
        <BorderedSection
          title={`Level ${level} Dependency Mapping`}
          noPadding
          extendOptions={
            canEdit ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAddComment}
                  className="text-blue underline hover:cursor-pointer font-normal"
                  aria-label="Add / Edit Commentary"
                >
                  Add / Edit Commentary
                </button>
                <Comment />
              </div>
            ) : null
          }
        >
          <TableV3
            title={`Level ${level} Dependency Mapping`}
            columns={columns}
            data={dependenciesData}
            loading={loading || loadingStates}
            editable
            formFields={formFields}
            modifyVariables={modifyVariables}
            customStyles="nested-columns--custom custom-table--dependency-mapping"
            inlineEditing
            useBorderedSection={false}
            editableOptions={{
              update: canEdit,
            }}
            isMatrix
            scroll={{ x: 800, y: 340 }}
            exportExcelColumns={[...baseColumns, ...mappingColumns]}
          />
        </BorderedSection>
      </QueryErrorBoundary>
      <Footer
        backPath={`/dependency-mapping/${divisionId}/${parentLocation}/level-locations?divisionName=${divisionName}&locationId=${locationId}&locationName=${locationName}&level=${level}&prevLocationId=${updatedPrevLocationId}&prevLocationName=${updatedPrevLocationName}`}
        nextPath={`/dependency-mapping/${parentLocation}/level-summary/${divisionId}?level=${level}&locationId=${locationId}&divisionName=${divisionName}&locationName=${locationName}&prevLocationId=${updatedPrevLocationId}&prevLocationName=${updatedPrevLocationName}`}
      />
      <AntModal
        title={`Level ${level} Intermediate Dependencies`}
        openModal={modalOpen}
        onCancel={handleCancel}
        width={750}
      >
        <LevelMappingForm
          onCancel={handleCancel}
          data={item}
          parentLocation={parentLocation}
          product={product}
          level={level}
          locationLevel={locationId}
          isEditing={isEditing}
        />
      </AntModal>
    </div>
  );
}

export default LevelLocationMapping;
